/**
 * CustomerDetailView - Redesigned as decision hub, not archive
 * Implements clear hierarchy: Header → Metrics → Actions → Timeline → Tabs → Collapsed
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Schedule,
  Add,
  Note,
  WhatsApp,
  Phone,
  Email,
  LocationOn,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { Tag, Statistic, Row, Col, Badge } from "antd";
import { styled } from "@mui/material/styles";
import RelatedLeadsTable from "./RelatedLeadsTable";
import RelatedQuotesTable from "./RelatedQuotesTable";
import RelatedOrdersTable from "./RelatedOrdersTable";
import RelatedBookingsTable from "./RelatedBookingsTable";
import RelatedInvoicesTable from "./RelatedInvoicesTable";
import RelatedActivity from "./RelatedActivity";
import CustomerEditModal from "./CustomerEditModal";
import CustomerNotesPanel from "./CustomerNotesPanel";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import { getCustomer } from "../services/customerService";
import { useFollowUps } from "../../followups/hooks/useFollowUps.js";
import { formatCurrency } from "@/shared/utils/formatters";
import { getCustomerHealth } from "../utils/customerUtils";
import api from "@/shared/api";
import {
  JobJourneyStrip,
  buildCustomerJourneys,
  seedFromCustomer,
  selectCustomerJourney,
} from "@/shared/jobJourney/index.js";

// Styled Components
const ProfileHeader = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  background: "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)",
  border: "1px solid #e0e0e0",
  borderRadius: 12,
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: theme.spacing(3),
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const CustomerInfo = styled(Box)({
  flex: 1,
});

const CustomerName = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: "#1a1a1a",
}));

const ContactInfo = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: "#666",
  fontSize: "0.95rem",
}));

const HealthBadge = styled(Chip)(({ health }) => {
  const colors = {
    active: { bg: "#e8f5e9", color: "#2e7d32", label: "🟢 Active" },
    dormant: { bg: "#fff3e0", color: "#e65100", label: "🟡 Dormant" },
    risk: { bg: "#ffebee", color: "#c62828", label: "🔴 Risk" },
  };
  const style = colors[health] || colors.active;
  return {
    backgroundColor: style.bg,
    color: style.color,
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: "8px 16px",
    height: "auto",
  };
});

const MetricsStrip = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: "#f8f9fa",
  border: "1px solid #e0e0e0",
  borderRadius: 8,
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  borderRight: "1px solid #e0e0e0",
  "&:last-child": {
    borderRight: "none",
  },
}));

const MetricValue = styled(Typography)({
  fontSize: "1.75rem",
  fontWeight: 700,
  color: "#1a1a1a",
  marginBottom: 4,
});

const MetricLabel = styled(Typography)({
  fontSize: "0.875rem",
  color: "#666",
  fontWeight: 500,
});

const MetricSubtext = styled(Typography)({
  fontSize: "0.75rem",
  color: "#999",
  marginTop: 4,
});

const ActionsBar = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: "#ffffff",
  borderRadius: 8,
  border: "1px solid #e0e0e0",
  position: "sticky",
  top: 16,
  zIndex: 10,
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    position: "relative",
    top: 0,
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  textTransform: "none",
  padding: "10px 24px",
  borderRadius: 8,
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  fontWeight: 500,
  textTransform: "none",
  padding: "8px 16px",
  borderRadius: 8,
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
  borderRadius: 8,
  boxShadow: "none",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#1a1a1a",
  marginBottom: theme.spacing(1),
}));

const CollapsibleSection = styled(Collapse)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiCollapse-wrapper": {
    paddingTop: theme.spacing(2),
  },
}));

/**
 * CustomerDetailView Component
 */
const CustomerDetailView = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [bookingsExpanded, setBookingsExpanded] = useState(false);
  const [notes, setNotes] = useState([]);

  // State
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const selectedLeadId = searchParams.get("leadId");
  const selectedQuoteId = searchParams.get("quoteId");

  // Fetch follow-ups
  const { data: followUpsData } = useFollowUps({
    entityType: "customer",
    entityId: customerId,
  });
  const followUps = followUpsData?.followUps || [];

  const customerJourneys = useMemo(
    () =>
      buildCustomerJourneys({
        leads: customer?.leads || [],
        quotes: customer?.quotes || [],
        invoices: customer?.invoices || [],
        orders: customer?.orders || [],
      }),
    [customer]
  );

  const activeJourney = useMemo(
    () =>
      selectCustomerJourney(customerJourneys, {
        leadId: selectedLeadId,
        quoteId: selectedQuoteId,
      }),
    [customerJourneys, selectedLeadId, selectedQuoteId]
  );

  const jobJourneyLinks = useMemo(
    () => (customer ? seedFromCustomer(customer, activeJourney) : null),
    [customer, activeJourney]
  );

  const handleJourneyChange = useCallback(
    (event) => {
      const key = event.target.value;
      const journey = customerJourneys.find((j) => j.key === key);
      if (!journey) return;
      const next = new URLSearchParams(searchParams);
      if (journey.leadId) next.set("leadId", journey.leadId);
      else next.delete("leadId");
      if (journey.quoteId) next.set("quoteId", journey.quoteId);
      else next.delete("quoteId");
      setSearchParams(next, { replace: true });
    },
    [customerJourneys, searchParams, setSearchParams]
  );

  // Fetch customer notes for health detection
  useEffect(() => {
    const fetchNotes = async () => {
      if (!customerId) return;
      try {
        const response = await api.get(`/customers/${customerId}/notes`, {
          params: { includeArchived: false },
        });
        if (response.success && Array.isArray(response.data)) {
          setNotes(response.data);
        }
      } catch (err) {
        // Silently fail - notes are optional for health detection
        console.error("Failed to fetch notes for health detection:", err);
      }
    };
    if (customerId) {
      fetchNotes();
    }
  }, [customerId]);

  // Fetch customer data
  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getCustomer(customerId, true);
      if (result.success) {
        setCustomer(result.data);
      } else {
        setError(result.error || "Failed to load customer");
      }
    } catch (err) {
      setError(err.message || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  // Handlers
  const handleBack = useCallback(() => {
    navigate("/customers");
  }, [navigate]);

  const handleEdit = useCallback(() => {
    setEditModalOpen(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditModalOpen(false);
    fetchCustomer();
  }, [fetchCustomer]);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
  }, []);

  // Calculate customer health
  const customerHealth = useMemo(() => {
    if (!customer) return "active";
    return getCustomerHealth(customer, notes);
  }, [customer, notes]);

  // Calculate meaningful metrics
  const metrics = useMemo(() => {
    if (!customer) return null;

    const leads = customer.leads || [];
    const quotes = customer.quotes || [];
    const invoices = customer.invoices || [];
    const orders = customer.orders || [];

    const acceptedLeads = leads.filter((l) =>
      ["accepted", "converted"].includes(l.status)
    ).length;

    const pendingQuotes = quotes.filter((q) => q.status === "pending").length;

    const totalInvoiceAmount = invoices.reduce(
      (sum, inv) => sum + (parseFloat(inv.total) || 0),
      0
    );

    return {
      leads: {
        total: leads.length,
        subtext: acceptedLeads > 0 ? `${acceptedLeads} accepted` : "No accepted",
      },
      quotes: {
        total: quotes.length,
        subtext: pendingQuotes > 0 ? `${pendingQuotes} pending` : "No pending",
      },
      invoices: {
        total: invoices.length,
        subtext:
          totalInvoiceAmount > 0
            ? formatCurrency(totalInvoiceAmount)
            : "No amount",
      },
      orders: {
        total: orders.length,
        subtext: orders.length > 0 ? `${orders.length} total` : "No orders",
      },
    };
  }, [customer]);

  // Get city from address
  const getCity = (address) => {
    if (!address) return null;
    // Simple extraction - adjust based on your address format
    const parts = address.split(",");
    return parts.length > 1 ? parts[parts.length - 2].trim() : null;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box py={3}>
          <Alert severity="error">Error: {error}</Alert>
        </Box>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="lg">
        <Box py={3}>
          <Alert severity="warning">Customer not found</Alert>
        </Box>
      </Container>
    );
  }

  const leads = customer.leads || [];
  const quotes = customer.quotes || [];
  const orders = customer.orders || [];
  const bookings = customer.bookings || [];
  const invoices = customer.invoices || [];

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        {/* Back Button */}
        <Box mb={2}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back to Customers
          </Button>
        </Box>

        {/* Customer Profile Header */}
        <ProfileHeader>
          <HeaderContent>
            <CustomerInfo>
              <CustomerName variant="h1">
                {customer.name || "Unnamed Customer"}
              </CustomerName>
              <ContactInfo>
                {customer.phone && !customer.phone.startsWith("TEMP_") && (
                  <ContactItem>
                    <Phone fontSize="small" />
                    <Typography component="span">{customer.phone}</Typography>
                    <Tooltip title="Open WhatsApp">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const whatsappUrl = `https://wa.me/${customer.phone.replace(/\D/g, "")}`;
                          window.open(whatsappUrl, "_blank");
                        }}
                      >
                        <WhatsApp fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ContactItem>
                )}
                {customer.email && (
                  <ContactItem>
                    <Email fontSize="small" />
                    <Typography component="span">{customer.email}</Typography>
                  </ContactItem>
                )}
                {customer.address && (
                  <ContactItem>
                    <LocationOn fontSize="small" />
                    <Typography component="span">
                      {getCity(customer.address) || customer.address}
                    </Typography>
                  </ContactItem>
                )}
              </ContactInfo>
            </CustomerInfo>
            <Box>
              <HealthBadge
                health={customerHealth}
                label={
                  customerHealth === "active"
                    ? "🟢 Active"
                    : customerHealth === "risk"
                    ? "🔴 Risk"
                    : "🟡 Dormant"
                }
              />
            </Box>
          </HeaderContent>
        </ProfileHeader>

        {jobJourneyLinks ? (
          <Box mb={3}>
            {customerJourneys.length > 1 ? (
              <FormControl size="small" fullWidth sx={{ mb: 1.5, maxWidth: 480 }}>
                <InputLabel id="customer-journey-select-label">
                  Active journey
                </InputLabel>
                <Select
                  labelId="customer-journey-select-label"
                  label="Active journey"
                  value={activeJourney?.key || ""}
                  onChange={handleJourneyChange}
                >
                  {customerJourneys.map((journey) => (
                    <MenuItem key={journey.key} value={journey.key}>
                      {journey.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <JobJourneyStrip
              current="customer"
              customerId={jobJourneyLinks.customerId}
              leadId={jobJourneyLinks.leadId}
              quoteId={jobJourneyLinks.quoteId}
              invoiceId={jobJourneyLinks.invoiceId}
              orderId={jobJourneyLinks.orderId}
              customerDisplay={jobJourneyLinks.customerDisplay}
              leadDisplay={jobJourneyLinks.leadDisplay}
              quoteDisplay={jobJourneyLinks.quoteDisplay}
              invoiceDisplay={jobJourneyLinks.invoiceDisplay}
              orderDisplay={jobJourneyLinks.orderDisplay}
            />
          </Box>
        ) : null}

        {/* Metrics Strip */}
        {metrics && (
          <MetricsStrip>
            <CardContent>
              <Row gutter={16}>
                <Col span={6}>
                  <MetricCard>
                    <MetricValue>{metrics.leads.total}</MetricValue>
                    <MetricLabel>Leads</MetricLabel>
                    <MetricSubtext>{metrics.leads.subtext}</MetricSubtext>
                  </MetricCard>
                </Col>
                <Col span={6}>
                  <MetricCard>
                    <MetricValue>{metrics.quotes.total}</MetricValue>
                    <MetricLabel>Quotes</MetricLabel>
                    <MetricSubtext>{metrics.quotes.subtext}</MetricSubtext>
                  </MetricCard>
                </Col>
                <Col span={6}>
                  <MetricCard>
                    <MetricValue>{metrics.invoices.total}</MetricValue>
                    <MetricLabel>Invoices</MetricLabel>
                    <MetricSubtext>{metrics.invoices.subtext}</MetricSubtext>
                  </MetricCard>
                </Col>
                <Col span={6}>
                  <MetricCard>
                    <MetricValue>{metrics.orders.total}</MetricValue>
                    <MetricLabel>Orders</MetricLabel>
                    <MetricSubtext>{metrics.orders.subtext}</MetricSubtext>
                  </MetricCard>
                </Col>
              </Row>
            </CardContent>
          </MetricsStrip>
        )}

        {/* Primary Actions Bar */}
        <ActionsBar>
          <PrimaryButton
            variant="contained"
            startIcon={<Schedule />}
            onClick={() =>
              navigate(
                `/followups/new?entityType=customer&entityId=${customerId}`
              )
            }
          >
            Create Follow-up
          </PrimaryButton>
          <SecondaryButton
            variant="outlined"
            startIcon={<Add />}
            onClick={() => navigate(`/quotes/new?customerId=${customerId}`)}
          >
            New Quote
          </SecondaryButton>
          <SecondaryButton
            variant="outlined"
            startIcon={<Note />}
            onClick={() => {
              // Scroll to notes section
              const notesSection = document.getElementById("customer-notes");
              if (notesSection) {
                notesSection.scrollIntoView({ behavior: "smooth" });
                // Small delay to ensure scroll completes before focusing
                setTimeout(() => {
                  const addButton = notesSection.querySelector(
                    'button[type="button"]'
                  );
                  if (addButton) addButton.focus();
                }, 500);
              }
            }}
          >
            Add Note
          </SecondaryButton>
          <Box sx={{ flexGrow: 1 }} />
          <SecondaryButton
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Edit Customer
          </SecondaryButton>
        </ActionsBar>

        {/* Activity Timeline (Smart) */}
        <SectionCard>
          <CardContent>
            <SectionTitle>Recent Activity</SectionTitle>
            <Divider sx={{ mb: 2 }} />
            <ConfigProvider theme={bubakiiTheme}>
              <RelatedActivity
                leads={leads.slice(0, 5)}
                quotes={quotes.slice(0, 5)}
                invoices={invoices.slice(0, 5)}
                followUps={followUps.slice(0, 5)}
              />
            </ConfigProvider>
            {(leads.length > 5 ||
              quotes.length > 5 ||
              invoices.length > 5 ||
              followUps.length > 5) && (
              <Box mt={2} textAlign="center">
                <Button
                  variant="text"
                  onClick={() => {
                    document
                      .getElementById("commercial-records")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  View All Activity →
                </Button>
              </Box>
            )}
          </CardContent>
        </SectionCard>

        {/* Commercial Records (Tabbed) */}
        <SectionCard id="commercial-records">
          <CardContent>
            <SectionTitle>Commercial Records</SectionTitle>
            <Divider sx={{ mb: 2 }} />
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                },
              }}
            >
              <Tab
                label={
                  <Badge count={leads.length} size="small" offset={[8, 0]}>
                    Leads
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge count={quotes.length} size="small" offset={[8, 0]}>
                    Quotes
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge count={invoices.length} size="small" offset={[8, 0]}>
                    Invoices
                  </Badge>
                }
              />
            </Tabs>
            <Box mt={2}>
              {activeTab === 0 && (
                <>
                  <RelatedLeadsTable leads={leads.slice(0, 5)} />
                  {leads.length > 5 && (
                    <Box mt={2} textAlign="center">
                      <Button
                        variant="text"
                        onClick={() =>
                          navigate(`/leads?customerId=${customerId}`)
                        }
                      >
                        View All {leads.length} Leads →
                      </Button>
                    </Box>
                  )}
                </>
              )}
              {activeTab === 1 && (
                <>
                  <RelatedQuotesTable quotes={quotes.slice(0, 5)} />
                  {quotes.length > 5 && (
                    <Box mt={2} textAlign="center">
                      <Button
                        variant="text"
                        onClick={() =>
                          navigate(`/quotes?customerId=${customerId}`)
                        }
                      >
                        View All {quotes.length} Quotes →
                      </Button>
                    </Box>
                  )}
                </>
              )}
              {activeTab === 2 && (
                <>
                  <RelatedInvoicesTable invoices={invoices.slice(0, 5)} />
                  {invoices.length > 5 && (
                    <Box mt={2} textAlign="center">
                      <Button
                        variant="text"
                        onClick={() =>
                          navigate(`/invoices?customerId=${customerId}`)
                        }
                      >
                        View All {invoices.length} Invoices →
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </CardContent>
        </SectionCard>

        {/* Customer Notes (Integrated Timeline) */}
        <SectionCard id="customer-notes">
          <CardContent>
            <ConfigProvider theme={bubakiiTheme}>
              <CustomerNotesPanel
                customerId={customer.customerId}
                context="all"
                entityType="general"
              />
            </ConfigProvider>
          </CardContent>
        </SectionCard>

        {/* Operational Records (Collapsed) */}
        {(orders.length > 0 || bookings.length > 0) && (
          <SectionCard>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <SectionTitle>Operational Records</SectionTitle>
                <IconButton
                  onClick={() => {
                    setOrdersExpanded(!ordersExpanded);
                    setBookingsExpanded(!bookingsExpanded);
                  }}
                >
                  {ordersExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <CollapsibleSection in={ordersExpanded}>
                {orders.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Orders ({orders.length})
                    </Typography>
                    <RelatedOrdersTable orders={orders} />
                  </Box>
                )}
                {bookings.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Bookings ({bookings.length})
                    </Typography>
                    <RelatedBookingsTable bookings={bookings} />
                  </Box>
                )}
              </CollapsibleSection>
            </CardContent>
          </SectionCard>
        )}

        {/* Edit Modal */}
        {editModalOpen && (
          <CustomerEditModal
            customer={customer}
            open={editModalOpen}
            onClose={handleCloseEditModal}
            onSuccess={handleEditSuccess}
          />
        )}
      </Box>
    </Container>
  );
};

export default CustomerDetailView;
